<script setup>
import Checkbox from '@/Components/Checkbox.vue';
import GuestLayout from '@/Layouts/GuestLayout.vue';
import InputError from '@/Components/InputError.vue';
import InputLabel from '@/Components/InputLabel.vue';
import PrimaryButton from '@/Components/PrimaryButton.vue';
import TextInput from '@/Components/TextInput.vue';
import ApplicationLogo from '@/Components/ApplicationLogo.vue';
import { Head, Link, useForm } from '@inertiajs/vue3';
import AuthLayout from "@/Layouts/AuthLayout.vue";
import Button from "@/Components/Button.vue";
import YandexCaptcha from "@/Components/YandexCaptcha.vue";
import {computed, ref} from "vue";

const ySmartCaptchaEnabled      = computed(() => import.meta.env.VITE_YANDEX_CAPTCHA_ENABLED.toLowerCase() === 'true');

defineProps({
    canResetPassword: Boolean,
    status: String,
});

const yCaptchaRef = ref(null);

const form = useForm({
    email: '',
    password: '',
    captchaToken: null,
    remember: false,
});

const submit = () => {
    if(ySmartCaptchaEnabled.value && !form.captchaToken) { yCaptchaRef.value.execute(); }
    else { sendForm(); }
};

const sendForm = () => {
    form.post(route('login'), {
        onFinish: () => {
            form.reset('password');
            form.reset('captchaToken');
        },
    });
}
</script>

<template>
    <AuthLayout>
        <Head title="Log in" />

        <div v-if="status" class="mb-4 font-medium text-sm text-green-600">
            {{ status }}
        </div>

        <form @submit.prevent="submit">
            <div>
                <InputLabel for="email" value="Email" />

                <TextInput
                    id="email"
                    type="email"
                    class="mt-1 block w-full"
                    v-model="form.email"
                    required
                    autofocus
                    autocomplete="username"
                />

                <InputError class="mt-2" :message="form.errors.email" />
            </div>

            <div class="mt-4">
                <InputLabel for="password" value="Пароль" />

                <TextInput
                    id="password"
                    type="password"
                    class="mt-1 block w-full"
                    v-model="form.password"
                    required
                    autocomplete="current-password"
                />

                <InputError class="mt-2" :message="form.errors.password" />
            </div>

            <div v-if="ySmartCaptchaEnabled" class="mt-4">
                <YandexCaptcha
                    ref="yCaptchaRef"
                    v-model="form.captchaToken"
                    :invisible="true"
                    @success="sendForm"
                />

                <InputError class="mt-2" :message="form.errors.captchaToken"/>
            </div>

            <div class="block mt-4 flex flex-row">
                <div class="basis-1/2">
                    <label class="flex items-center">
                        <Checkbox name="remember" v-model:checked="form.remember" />
                        <span class="ml-2 text-sm text-gray-600">Запомнить</span>
                    </label>
                </div>
                <div class="basis-1/2 text-right">
                    <Link
                        v-if="canResetPassword"
                        :href="route('password.request')"
                        class="underline text-sm text-gray-600 hover:text-gray-900 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                        Забыли пароль?
                    </Link>
                </div>
            </div>

            <div class="flex items-center justify-end mt-4 space-x-2">
                <Button class="b-text b-secondary"
                    type="link"
                    :link="route('register')"
                >
                    Регистрация
                </Button>

                <Button class="b-text b-primary" :class="{ 'opacity-25': form.processing }" :disabled="form.processing">
                    Войти
                </Button>
            </div>
        </form>
    </AuthLayout>

    <a href="https://yandex.ru/legal/smartcaptcha_notice/" target="_blank" class="absolute bottom-4 right-0 bg-gray-300/30 rounded-l-full px-2 py-1 text-[0.6rem] text-right">
        Условия обработки персональных данных<br/>
        Сервисом Yandex SmartCaptcha
    </a>
</template>
